import type { LanguageModelV1Source } from '@ai-sdk/provider';
import { XMLParser } from 'fast-xml-parser';
import { memo, useMemo, useRef, type ReactNode } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { Markdown } from './Markdown';
import { QuickAction } from './QuickAction';
import { Thinking } from '~/components/chat/Thinking';
import { Link } from '~/components/ui/Link';
import { workbenchStore } from '~/lib/stores/workbench';
import type { Message } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import { createScopedLogger } from '~/utils/logger';
import {
  BOLT_QUICK_ACTIONS_REGEX,
  SUPPORTED_QUICK_ACTIONS,
  type QuickActionList,
  type QuickActionUnion,
} from '~/utils/quick-actions';
import { addWorkDir } from '~/utils/workDir';

interface AssistantMessageProps {
  className?: string;
  content: string;
  annotations?: Message['annotations'];
  parts?: Message['parts'];
  disableQuickActions?: boolean;
  actionButtons?: ReactNode;
  sendMessage?: (messageInput?: string) => void;
  scrollToBottom: ScrollToBottom;
}

const logger = createScopedLogger('AssistantMessage');

export const AssistantMessage = memo(
  ({
    className,
    content,
    annotations,
    parts,
    disableQuickActions,
    actionButtons,
    sendMessage,
    scrollToBottom,
  }: AssistantMessageProps) => {
    const sources = parts?.filter((part) => part.type === 'source') ?? [];

    const { markdownContent, quickActions } = useQuickActions(
      content,
      disableQuickActions,
      sendMessage,
      scrollToBottom,
    );

    return (
      <div className={classNames('flex flex-col gap-4 overflow-hidden w-full', className)}>
        {/* needs to be the same height as the action items */}
        <div className="flex items-center min-h-6.5 select-none">
          <h3 className="font-semibold flex-1">Bolt</h3>
          {actionButtons}
        </div>

        {sources.length > 0 && (
          <div>
            <small className="font-semibold inline-block mb-1">Sources</small>
            <div className="flex flex-wrap gap-2">
              {sources.map((part, index) => (
                <SearchSource key={part.source.id} index={index} source={part.source} />
              ))}
            </div>
          </div>
        )}

        <Thinking annotations={annotations} parts={parts} content={content} />

        {markdownContent && (
          <Markdown html scrollToBottom={scrollToBottom}>
            {markdownContent}
          </Markdown>
        )}

        {quickActions.length > 0 && <div className="flex items-center gap-2 flex-wrap">{quickActions}</div>}
      </div>
    );
  },
);

function useQuickActions(
  content: string,
  disableQuickActions?: boolean,
  sendMessage?: (messageInput?: string) => void,
  scrollToBottom?: ScrollToBottom,
) {
  const parser = new XMLParser({ ignoreAttributes: false, attributeNamePrefix: '', textNodeName: 'text' });

  const previousQuickActionsRef = useRef<{ match: string; disableQuickActions?: boolean } | undefined>();
  const cacheRef = useRef<{ markdownContent: string; quickActions: ReactNode[] }>();

  return useMemo(() => {
    let markdownContent = content;
    let quickActions: ReactNode[] = [];

    const match = content.match(BOLT_QUICK_ACTIONS_REGEX);
    const currentMatch = match?.[1];

    if (!currentMatch) {
      return { markdownContent, quickActions };
    }

    if (
      cacheRef.current &&
      currentMatch === previousQuickActionsRef.current?.match &&
      previousQuickActionsRef.current?.disableQuickActions === disableQuickActions
    ) {
      return cacheRef.current;
    }

    previousQuickActionsRef.current = { match: currentMatch, disableQuickActions };

    const result = parser.parse(match[1]) as Record<string, QuickActionUnion | QuickActionList | undefined | null>;

    logger.debug('Parse markdown content');

    const parsedQuickActions = result['bolt-quick-action'];

    const boltQuickActions = !parsedQuickActions
      ? []
      : Array.isArray(parsedQuickActions)
        ? parsedQuickActions
        : [parsedQuickActions];

    quickActions = boltQuickActions.flatMap((action, index) => {
      if (!isValidQuickAction(action)) {
        return [];
      }

      return (
        <QuickAction
          key={`${action.type}:${index}`}
          action={action}
          disabled={disableQuickActions}
          sendMessage={sendMessage}
          scrollToBottom={scrollToBottom}
        />
      );
    });

    markdownContent = content.replace(BOLT_QUICK_ACTIONS_REGEX, '').trimEnd();

    cacheRef.current = { markdownContent, quickActions };

    return cacheRef.current;
  }, [content, disableQuickActions, sendMessage, scrollToBottom]);
}

interface SearchSourceProps {
  index: number;
  source: LanguageModelV1Source;
}

const SearchSource = memo<SearchSourceProps>(({ index, source }) => {
  return (
    <Link
      className="text-xs bg-bolt-elements-background-depth-3 dark:bg-bolt-elements-background-depth-4 text-bolt-elements-textSecondary hover:text-bolt-elements-textPrimary transition-theme py-0.5 px-2 rounded-md max-w-[200px] truncate"
      href={source.url}
      target="_blank"
    >
      {source.title || index}
    </Link>
  );
});

function isValidQuickAction(action: QuickActionUnion) {
  const files = workbenchStore.files.get();

  const isValidMessageAction = (action.type === 'implement' || action.type === 'message') && action.message?.trim();
  const isValidFileAction = action.type === 'file' && files[addWorkDir(action.path)]?.type === 'file';
  const isValidLinkAction = action.type === 'link' && action.href;

  return (
    SUPPORTED_QUICK_ACTIONS.has(action.type) &&
    action.text?.trim() &&
    (isValidMessageAction || isValidFileAction || isValidLinkAction)
  );
}
